import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Skeleton } from "@/components/ui/skeleton";

import { usePartners } from "../api";

import { fmtMoney } from "./_format";

/**
 * Compact quick-view for a partner. Reads the partner row out of the cached
 * partners list rather than hitting an extra endpoint — the partners list is
 * almost always already loaded by the time someone clicks into a row.
 */
export function PartnerQuickInfo({ accountId }: { accountId: string }) {
  const { data, isLoading } = usePartners();
  const row = data?.rows.find((r) => r.account_id === accountId);

  if (isLoading) {
    return (
      <div className="space-y-3 p-5">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex justify-between gap-3">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-32" />
          </div>
        ))}
      </div>
    );
  }
  if (!row) {
    return <div className="p-5 text-sm text-muted-foreground">Partner not found.</div>;
  }
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-3 p-5 text-sm">
      <Field label="Partner" value={row.account_name} />
      <Field label="Type" value={row.master_type ?? "—"} />
      <Field label="Level" value={<StatusBadge value={row.partner_level || "default"} />} />
      <Field label="Industry" value={row.industry_name ?? "—"} />
      <Field label="Closer" value={row.closer_name ?? "—"} />
      <Field
        label="Current ARR"
        value={<span className="tabular-nums">{fmtMoney(row.current_arr)}</span>}
      />
      <Field
        label="ARR 12mo Δ"
        value={
          <span className="tabular-nums" data-delta={row.arr_t12m_delta >= 0 ? "pos" : "neg"}>
            {row.arr_t12m_delta >= 0 ? "+" : ""}
            {fmtMoney(row.arr_t12m_delta)}
          </span>
        }
      />
      <Field label="POs" value={row.po_count} />
      <Field label="Companies" value={row.company_count} />
      <Field
        label="Billing"
        value={<StatusBadge value={row.billing_status || "default"} />}
      />
    </dl>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="text-foreground">{value}</dd>
    </>
  );
}
